import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { obtenerCotizacion, limpiarDetalleCotizacion, generarPdfCotizacion } from '../../../actions/cotizaciones';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const renderBadgeStatus = (status) => (
    status
        ? <span className="badge bg-success">Autorizada</span>
        : <span className="badge bg-warning text-dark">Pendiente</span>
);

const DetalleCotizacion = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { detalle: cotizacion, loadingDetalle } = useSelector(state => state.cotizaciones);

    const [paquetesExpandidos, setPaquetesExpandidos] = useState({});

    const togglePaqueteExpandido = (key) => {
        setPaquetesExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        if (!id) return;

        dispatch(obtenerCotizacion(id));

        return () => {
            dispatch(limpiarDetalleCotizacion());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Proyectos', path: '/proyectos' },
            { activo: false, nombre: cotizacion?.proyecto?.nombre || 'Proyecto', path: `/proyectos/detalle/${cotizacion?.proyecto?._id || ''}` },
            { activo: true, nombre: `Cotización ${cotizacion?.folio || ''}`.trim(), path: `/proyectos/cotizaciones/detalle/${id}` }
        ]));
    }, [dispatch, id, cotizacion]);

    if (loadingDetalle || !cotizacion) {
        return (
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Cargando cotización...</h3>
                </div>
            </div>
        );
    }

    const productos = Array.isArray(cotizacion.productos) ? cotizacion.productos : [];
    const conceptos = Array.isArray(cotizacion.conceptos) ? cotizacion.conceptos : [];
    const paquetes = Array.isArray(cotizacion.paquetes) ? cotizacion.paquetes : [];

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <div className="d-flex align-items-center justify-content-between mb-1 mt-5">
                        <div className="d-flex align-items-center gap-2">
                            <h3 className="mb-0">Cotización {cotizacion?.folio || ''}</h3>
                            {renderBadgeStatus(cotizacion?.status)}
                        </div>

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => dispatch(generarPdfCotizacion(id))}
                        >
                            <i className="fas fa-file-pdf" /> Generar PDF
                        </button>
                    </div>
                    <h6 className="text-muted mb-4">{cotizacion?.proyecto?.nombre || 'Proyecto'}</h6>

                    {(cotizacion?.nombre || cotizacion?.descripcion) && (
                        <div className="row mb-4">
                            {cotizacion?.nombre && (
                                <div className="col-md-4">
                                    <div className="text-muted small">Nombre</div>
                                    <div>{cotizacion.nombre}</div>
                                </div>
                            )}
                            {cotizacion?.descripcion && (
                                <div className="col-md-8">
                                    <div className="text-muted small">Descripción</div>
                                    <div>{cotizacion.descripcion}</div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small">Fecha</div>
                            <div>{formatDate(cotizacion?.fecha)}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Versión</div>
                            <div>{cotizacion?.version || 1}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Dirección</div>
                            <div>{cotizacion?.proyecto?.direccion || 'Sin definir'}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Total</div>
                            <div className="fw-bold">{formatMoney(cotizacion?.total)}</div>
                        </div>
                    </div>

                    <h5 className="mb-3">Productos</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Proveedor</th>
                                    <th>Unidad</th>
                                    <th className="text-end">Cantidad</th>
                                    <th className="text-end">Precio compra</th>
                                    <th className="text-end">Precio venta</th>
                                    <th className="text-end">Precio cotizado</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productos.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center text-muted">
                                            Sin productos
                                        </td>
                                    </tr>
                                )}

                                {productos.map((p) => {
                                    const importe = Number(p.cantidad || 0) * Number(p.precio_cotizado || 0) * (p.iva ? 1.16 : 1);
                                    return (
                                        <tr key={p._id}>
                                            <td>{p.producto?.nombre || 'Sin definir'}</td>
                                            <td>{p.proveedor?.nombre || '—'}</td>
                                            <td>{p.unidad?.nombre || '—'}</td>
                                            <td className="text-end">{p.cantidad}</td>
                                            <td className="text-end">{formatMoney(p.precio_compra)}</td>
                                            <td className="text-end">{formatMoney(p.precio_venta)}</td>
                                            <td className="text-end">{formatMoney(p.precio_cotizado)}</td>
                                            <td className="text-center">{p.iva ? 'Sí' : 'No'}</td>
                                            <td className="text-end">{formatMoney(importe)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Conceptos</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Detalle</th>
                                    <th className="text-end">Cantidad</th>
                                    <th>Unidad</th>
                                    <th className="text-end">Precio unitario</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conceptos.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Sin conceptos
                                        </td>
                                    </tr>
                                )}

                                {conceptos.map((c) => {
                                    const base = Number(c.cantidad || 0) * Number(c.precio_unitario || 0);
                                    const importe = c.iva ? base * 1.16 : base;
                                    return (
                                        <tr key={c._id}>
                                            <td>{Number(c.tipo) === 1 ? 'Producto' : 'Servicio'}</td>
                                            <td>{c.detalle}</td>
                                            <td className="text-end">{c.cantidad}</td>
                                            <td>{c.unidad?.nombre || '—'}</td>
                                            <td className="text-end">{formatMoney(c.precio_unitario)}</td>
                                            <td className="text-center">{c.iva ? 'Sí' : 'No'}</td>
                                            <td className="text-end">{formatMoney(importe)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Paquetes</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }} />
                                    <th>Paquete</th>
                                    <th className="text-end">Cantidad</th>
                                    <th className="text-end">Precio cotizado</th>
                                    <th className="text-center">IVA</th>
                                    <th className="text-end">Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paquetes.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Sin paquetes
                                        </td>
                                    </tr>
                                )}

                                {paquetes.map((p) => {
                                    const cantidad = Number(p.cantidad || 0);
                                    const importe = cantidad * Number(p.precio_cotizado || 0) * (p.iva ? 1.16 : 1);
                                    const componentes = Array.isArray(p.componentes) ? p.componentes : [];
                                    const abierto = !!paquetesExpandidos[p._id];

                                    return (
                                        <Fragment key={p._id}>
                                            <tr>
                                                <td className="text-center align-middle">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link p-0"
                                                        onClick={() => togglePaqueteExpandido(p._id)}
                                                        title={abierto ? 'Ocultar productos del paquete' : 'Ver productos del paquete'}
                                                    >
                                                        <i className={`fas fa-chevron-${abierto ? 'down' : 'right'}`} />
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">{p.paquete?.nombre || 'Sin definir'}</div>
                                                    {p.paquete?.codigo ? (
                                                        <div className="text-muted" style={{ fontSize: 12 }}>{p.paquete.codigo}</div>
                                                    ) : null}
                                                </td>
                                                <td className="text-end">{cantidad}</td>
                                                <td className="text-end">{formatMoney(p.precio_cotizado)}</td>
                                                <td className="text-center">{p.iva ? 'Sí' : 'No'}</td>
                                                <td className="text-end">{formatMoney(importe)}</td>
                                            </tr>
                                            {abierto && (
                                                <tr>
                                                    <td />
                                                    <td colSpan={5} className="p-0">
                                                        <div className="table-responsive m-2">
                                                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Producto</th>
                                                                        <th className="text-center" style={{ width: 130 }}>Cantidad</th>
                                                                        <th style={{ width: 150 }}>Unidad</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {componentes.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan={3} className="text-muted text-center">Sin productos</td>
                                                                        </tr>
                                                                    ) : componentes.map((c) => {
                                                                        const conversion = Number(c?.conversion || 1) || 1;
                                                                        const necesaria = (Number(c?.cantidad || 0) * cantidad) / conversion;

                                                                        return (
                                                                            <tr key={c._id}>
                                                                                <td>{c.nombre}</td>
                                                                                <td className="text-center">{necesaria}</td>
                                                                                <td>{c.unidad?.nombre || '-'}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="fs-5">
                            <span className="text-muted">Total: </span>
                            <span className="fw-bold">{formatMoney(cotizacion?.total)}</span>
                        </div>

                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/proyectos/detalle/${cotizacion?.proyecto?._id || ''}`)}
                            >
                                Volver
                            </button>

                            {!cotizacion?.status && (
                                <button
                                    type="button"
                                    className="btn btn-info text-white"
                                    onClick={() => navigate(`/proyectos/ordenes/nueva/${cotizacion?.proyecto?._id || ''}?cotizacion=${id}`)}
                                >
                                    <i className="fas fa-dolly" /> Generar orden de salida
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default DetalleCotizacion;
